import { APP_CONFIG } from "./config";

/**
 * GSC MONITORING SERVICE - V5.1 (Industrial Ingest + DSP Proofs)
 * 
 * ROLE: Capturer les anomalies DSP et les preuves techniques de rejet.
 */

export interface TelemetryEvent {
    level: 'info' | 'warning' | 'error' | 'fatal';
    category: 'dsp' | 'ui' | 'network' | 'system' | 'security';
    message: string;
    metadata?: Record<string, any>;
    tags?: Record<string, string>;
}

class MonitoringService {
    private isInitialized = false;
    private sessionId = crypto.randomUUID();
    private readonly ENDPOINT = "https://telemetry.guitarsocialclub.com/v2/ingest";

    public init() {
        if (this.isInitialized) return;
        this.isInitialized = true;

        if (APP_CONFIG.MONITORING.ENABLED) {
            console.log("[MONITORING] Industrial Telemetry Engine Active. Session:", this.sessionId);
            
            window.addEventListener('error', (event) => {
                this.captureException(event.error, { 
                    context: 'unhandled_runtime_error',
                    msg: event.message 
                });
            });

            window.addEventListener('unhandledrejection', (event) => {
                this.captureException(event.reason, { context: 'unhandled_async_rejection' });
            });
        }
    }

    /**
     * MONITORING DSP SYSTEMATIQUE
     * Logge les succès et surtout les preuves techniques des échecs.
     */
    public logDspSession(report: any) {
        if (!report) return;

        const status = report.scoring?.status || 'UNKNOWN';
        const isFailure = status === 'INVALID';
        const dspData = report.dsp_debug || {};
        const pipeline = report.pipeline || {};

        const payload: TelemetryEvent = {
            level: isFailure ? 'warning' : 'info',
            category: 'dsp',
            message: isFailure ? 'DSP_SIGNAL_REJECTED' : 'DSP_SESSION_SUCCESS',
            tags: {
                status,
                engine_v: APP_CONFIG.ENGINE_VERSION,
                origin: pipeline.origin || 'unknown',
                rejection_code: isFailure ? (pipeline.gate_reasons?.[0] || 'GENERIC_INVALID') : 'NONE'
            },
            metadata: {
                confidence: report.scoring?.confidence,
                rms_db: dspData.rms_db,
                stability: dspData.stability,
                onsets: dspData.onsets,
                clipping: dspData.clip_ratio,
                gate_reasons: pipeline.gate_reasons || [],
                capture_hash: pipeline.capture_hash,
                analysis_hash: pipeline.analysis_hash,
                // Preuves brutes pour recalibrage des seuils de bruit
                technical_proofs: pipeline.proof || null
            }
        };

        if (isFailure && APP_CONFIG.MONITORING.DEBUG_MODE) {
            console.warn(`[TELEMETRY] DSP Rejection Logged: ${status}`, payload.metadata);
        }

        this.send(payload);
    }

    public captureException(error: Error | any, context?: Record<string, any>) {
        const payload: TelemetryEvent = {
            level: 'error',
            category: 'system',
            message: error?.message || 'Anonymous Error',
            metadata: {
                stack: error?.stack,
                ...context
            }
        };
        
        this.send(payload);
    }

    private send(event: TelemetryEvent) {
        if (!APP_CONFIG.MONITORING.ENABLED) {
            if (APP_CONFIG.MONITORING.DEBUG_MODE && event.category !== 'dsp') {
                console.debug("[MONITORING-DEBUG]", event);
            }
            return;
        }

        const fullPayload = {
            session_id: this.sessionId,
            timestamp: new Date().toISOString(),
            app: "Guitar Match&Play",
            version: APP_CONFIG.VERSION,
            ...event
        };

        if (navigator.sendBeacon) {
            const blob = new Blob([JSON.stringify(fullPayload)], { type: 'application/json' });
            navigator.sendBeacon(this.ENDPOINT, blob);
        } else {
            fetch(this.ENDPOINT, {
                method: 'POST',
                body: JSON.stringify(fullPayload),
                keepalive: true,
                headers: { 'Content-Type': 'application/json' }
            }).catch(() => {});
        }
    }
}

export const monitoring = new MonitoringService();